import { Box, Typography, Paper, useTheme } from '@mui/material'
import { getBillingAmount, TIER_PRICING } from '@/utils/billingConstants'

export type SubscriptionFrequency = 'monthly' | 'quarterly' | 'annual'

interface SubscriptionSelectorProps {
  selectedFrequency: SubscriptionFrequency | null
  onFrequencySelect: (frequency: SubscriptionFrequency) => void
  tier: number
  // Invoice billing is never monthly — hide monthly when invoice is chosen.
  hideMonthly?: boolean
}

export default function SubscriptionSelector({
  selectedFrequency,
  onFrequencySelect,
  tier,
  hideMonthly = false,
}: SubscriptionSelectorProps) {
  const theme = useTheme()
  const pricing =
    TIER_PRICING[tier as keyof typeof TIER_PRICING] || TIER_PRICING[4]
  const monthlyPrice = pricing.monthly
  const annualTotal = getBillingAmount(monthlyPrice, 'annual')
  const annualUndiscountedTotal = monthlyPrice * 12
  const quarterlyTotal = getBillingAmount(monthlyPrice, 'quarterly')
  const annualMonthlyEquiv = Math.round((annualTotal / 12) * 100) / 100

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
        Choose Your Subscription
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {pricing.description} ({pricing.turnover} turnover)
      </Typography>

      <Box
        sx={{
          backgroundColor: '#fffde7',
          border: '1px solid #f9a825',
          borderRadius: 2,
          p: 2,
          mb: 3,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          Subscribe to an annual plan for these valuable benefits:
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {[
            '20% price discount compared to the monthly or quarterly plan',
            'See 12 months of matches (instead of 3) to support future planning',
            'Unlimited funding idea generation - analyses your mission  against available funding opportunities',
          ].map((benefit) => (
            <Typography
              key={benefit}
              component="li"
              variant="body2"
              sx={{ mb: 0.5 }}
            >
              {benefit}
            </Typography>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Annual — promoted */}
        <Paper
          elevation={selectedFrequency === 'annual' ? 3 : 1}
          onClick={() => onFrequencySelect('annual')}
          sx={{
            p: 4,
            cursor: 'pointer',
            border:
              selectedFrequency === 'annual'
                ? `2px solid ${theme.palette.primary.main}`
                : '1px solid #e0e0e0',
            borderRadius: 2,
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: '1.2rem' }}
              >
                Annual
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 400,
                  mt: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                ${annualMonthlyEquiv}/month — pay annually and save 20%
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  fontSize: '12px',
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  maxWidth: 300,
                }}
              >
                Lower cost, fewer payment admin tasks, and uninterrupted access
                all year.
              </Typography>
            </Box>
            <Box
              sx={{
                textAlign: 'right',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1,
                    justifyContent: 'flex-end',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      textDecoration: 'line-through',
                      color: 'text.secondary',
                    }}
                  >
                    ${annualUndiscountedTotal}/year
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color:
                        selectedFrequency === 'annual'
                          ? theme.palette.primary.main
                          : 'text.primary',
                    }}
                  >
                    ${annualTotal}/year
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  ex-GST
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Monthly — hidden for invoice billing */}
        {!hideMonthly && (
          <Paper
            elevation={selectedFrequency === 'monthly' ? 2 : 0}
            onClick={() => onFrequencySelect('monthly')}
            sx={{
              p: 3,
              cursor: 'pointer',
              border:
                selectedFrequency === 'monthly'
                  ? `2px solid ${theme.palette.primary.main}`
                  : '1px solid #e0e0e0',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Monthly
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Billed each month
                </Typography>
              </Box>
              <Box
                sx={{
                  textAlign: 'right',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color:
                        selectedFrequency === 'monthly'
                          ? theme.palette.primary.main
                          : 'text.primary',
                    }}
                  >
                    ${monthlyPrice}/month
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ex-GST
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Quarterly */}
        <Paper
          elevation={selectedFrequency === 'quarterly' ? 2 : 0}
          onClick={() => onFrequencySelect('quarterly')}
          sx={{
            p: 3,
            cursor: 'pointer',
            border:
              selectedFrequency === 'quarterly'
                ? `2px solid ${theme.palette.primary.main}`
                : '1px solid #e0e0e0',
            borderRadius: 2,
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Quarterly
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Billed every 3 months
              </Typography>
            </Box>
            <Box
              sx={{
                textAlign: 'right',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color:
                      selectedFrequency === 'quarterly'
                        ? theme.palette.primary.main
                        : 'text.primary',
                  }}
                >
                  ${quarterlyTotal}/quarter
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ex-GST
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}
